import { useEffect, useState, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { Mail } from 'lucide-react'
import { authApi } from '@/api/client'

const RESEND_COOLDOWN_SECONDS = 30
const CODE_LENGTH = 6

const INPUT_CLASSES =
  'w-full px-4 py-2.5 rounded-lg text-sm bg-white/5 border border-white/10 ' +
  'text-[var(--text-primary)] placeholder:text-[var(--text-muted)] ' +
  'focus:border-accent/60 focus:outline-none transition-colors'

const PRIMARY_BUTTON_CLASSES =
  'w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md ' +
  'bg-accent text-white font-medium text-sm hover:bg-accent/90 transition-colors ' +
  'disabled:opacity-50 disabled:cursor-not-allowed'

type Step = 'email' | 'code'

interface EmailCodeSignInProps {
  onSignedIn: () => void
}

function requestErrorMessage(error: unknown): string {
  if (isAxiosError(error) && error.response?.status === 400) {
    return 'Enter a valid email address.'
  }
  return 'We could not send a code right now. Please try again.'
}

function verifyErrorMessage(error: unknown): string {
  if (isAxiosError(error) && error.response?.status === 400) {
    return 'That code is wrong or has expired. Check it, or send a new one.'
  }
  return 'Something went wrong. Please try again.'
}

export default function EmailCodeSignIn({ onSignedIn }: EmailCodeSignInProps) {
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [cooldown, setCooldown] = useState(0)

  const requestCode = useMutation({
    mutationFn: (address: string) => authApi.requestCode(address),
    onSuccess: () => {
      setStep('code')
      setCode('')
      setCooldown(RESEND_COOLDOWN_SECONDS)
    },
  })

  const verifyCode = useMutation({
    mutationFn: (digits: string) => authApi.verifyCode(email, digits),
    onSuccess: onSignedIn,
    onError: () => setCode(''),
  })

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setTimeout(() => setCooldown(seconds => seconds - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  function submitEmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const address = email.trim()
    if (address) requestCode.mutate(address)
  }

  function changeCode(value: string) {
    const digits = value.replace(/\D/g, '').slice(0, CODE_LENGTH)
    setCode(digits)
    // Auto-submit on the sixth digit — the common path needs no button press.
    if (digits.length === CODE_LENGTH && !verifyCode.isPending) {
      verifyCode.mutate(digits)
    }
  }

  function switchEmail() {
    setStep('email')
    setCode('')
    requestCode.reset()
    verifyCode.reset()
  }

  if (step === 'email') {
    return (
      <form onSubmit={submitEmail} className="w-full flex flex-col gap-3 text-left">
        <label htmlFor="sign-in-email" className="text-xs text-[var(--text-secondary)]">
          Email
        </label>
        <input
          id="sign-in-email"
          type="email"
          required
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={event => setEmail(event.target.value)}
          className={INPUT_CLASSES}
        />
        <button type="submit" disabled={requestCode.isPending} className={PRIMARY_BUTTON_CLASSES}>
          <Mail size={16} aria-hidden />
          {requestCode.isPending ? 'Sending…' : 'Email me a sign-in code'}
        </button>
        {requestCode.isError && (
          <p role="alert" className="text-xs text-red-400">
            {requestErrorMessage(requestCode.error)}
          </p>
        )}
      </form>
    )
  }

  return (
    <div className="w-full flex flex-col gap-3 text-left">
      <p className="text-sm text-[var(--text-secondary)]">
        We sent a 6-digit code to{' '}
        <span className="text-[var(--text-primary)] font-medium">{email.trim()}</span>.
        It expires in 10 minutes.
      </p>
      <label htmlFor="sign-in-code" className="text-xs text-[var(--text-secondary)]">
        Code
      </label>
      <input
        id="sign-in-code"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        maxLength={CODE_LENGTH}
        placeholder="000000"
        value={code}
        disabled={verifyCode.isPending}
        onChange={event => changeCode(event.target.value)}
        className={`${INPUT_CLASSES} font-mono text-center text-lg tracking-[0.5em]`}
      />
      {verifyCode.isPending && (
        <p className="text-xs text-[var(--text-secondary)]">Signing you in…</p>
      )}
      {verifyCode.isError && (
        <p role="alert" className="text-xs text-red-400">
          {verifyErrorMessage(verifyCode.error)}
        </p>
      )}
      <div className="flex items-center justify-between text-xs">
        <button
          type="button"
          onClick={switchEmail}
          className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
        >
          Use a different email
        </button>
        <button
          type="button"
          disabled={cooldown > 0 || requestCode.isPending || verifyCode.isPending}
          onClick={() => requestCode.mutate(email.trim())}
          className="text-accent hover:text-accent/80 transition-colors disabled:text-[var(--text-muted)] disabled:cursor-not-allowed"
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
        </button>
      </div>
    </div>
  )
}
